import { z } from 'zod';
import { inquiryAddressSchema } from './address.js';
import { clientAnomalyEffectSchema } from '../inspection/anomaly-effect.js';
import { tabletBlockSchema } from '../inspection/tablet-blocks.js';
import { surveyV2Schema } from '../inspection/survey-v2.js';
import { personSchema } from '../people/person.js';
import { followUpStepSchema } from '../planning/passage-notice.js';
import { carriedMediaSchema } from '../sync/carried-medium.js';
import { submittedAnomalySchema } from '../sync/inspection-submission.js';
import { uuidV7Schema } from '../uuid.js';
import { buildingTypeSchema } from './building-type.js';
import { sewerNetworkTypeSchema } from './sewer-network-type.js';
import { inquiryStatusSchema } from './status.js';
/**
 * `Inquiry` — la demande de contrôle. Les cinq colonnes ajoutées le 21/09/2026 sont celles que
 * l'**écran d'import PDF** réclame et que le modèle n'avait pas ([M32](#m32)) : elles sont
 * **toutes nullables**, parce qu'un PDF mal imprimé donne une demande incomplète et qu'il vaut
 * mieux l'enregistrer que la refuser (M21). Le détail colonne par colonne est en M32.
 *
 * `applicantId` et `appointmentContactId` sont les deux rôles de personne que M15 a identifiés
 * comme manquants — le **demandeur payeur** et le **contact pour le rendez-vous**. Ils sont
 * distincts et souvent différents : sur une vente, le contact est fréquemment une agence
 * immobilière, et c'est elle que l'agent appelle. Les deux sont **nullables** : une demande
 * importée d'un fichier client arrive parfois sans coordonnées, et la refuser priverait le
 * back-office de la seule chose qu'il a (M21).
 *
 * Les deux références sont **libres et nullables** (M1) : l'unicité est portée par l'`id`, pas
 * par elles. `reference` est la « Réf. GHP », `caseReference` la référence du dossier côté
 * client — deux numérotations distinctes, le CDC le dit explicitement.
 */
export const inquirySchema = z.object({
    id: uuidV7Schema,
    reference: z.string().nullable(),
    caseReference: z.string().nullable(),
    /**
     * Le **lien vers le dossier client** (#410), saisi à la main au bureau. Texte libre : il ne devient
     * un lien cliquable que par `safeExternalUrl`. Il ne descend jamais sur la tablette.
     */
    caseUrl: z.string().nullable(),
    siteId: uuidV7Schema,
    teamId: uuidV7Schema.nullable(),
    applicantId: uuidV7Schema.nullable(),
    appointmentContactId: uuidV7Schema.nullable(),
    status: inquiryStatusSchema,
    /**
     * Le **type de contrôle** — « Vente », « SYAM », « Avant travaux »… **Champ libre** (M2, M13),
     * jamais une énumération : le vocabulaire vient du client, il est imprimé sur son rapport, et
     * il a déjà changé une fois. `INQUIRY_PURPOSES` en porte les six valeurs suggérées et
     * `inquiryPurposeLabel` le libellé, avec son repli sur la valeur brute.
     */
    purpose: z.string().nullable(),
    /**
     * Le **client donneur d'ordre** de cette demande — le champ « Client donneur d'ordre » que
     * l'import PDF corrige à la main.
     *
     * ⚠️ **C'est la demande qui le porte, pas seulement sa campagne.** `Site.lot_id → Lot.client_id`
     * (M14) dit de quelle campagne le lieu fait partie ; il ne dit rien d'une vente arrivée hors
     * campagne — et `Site.lot_id` est nullable exprès depuis le 13/09/2026, parce que c'est le cas
     * de la plupart des demandes. Quand les deux existent, c'est **celui-ci qui fait foi** (M32).
     */
    clientId: uuidV7Schema.nullable(),
    /** La première demande de la chaîne dont celle-ci est une contre-visite (#321, Q5). */
    followUpOfId: uuidV7Schema.nullable().default(null),
    /** Le rang de contre-visite — 1, 2… —, `null` pour un premier contrôle (#321, Q5). */
    followUpRank: z.number().int().positive().nullable().default(null),
    /**
     * L'« **En qualité de** » de la personne attendue sur place. Champ **libre** : l'écran propose
     * `CAPACITY_SUGGESTIONS` (#504), et toute autre qualité s'écrit.
     *
     * ⚠️ **Elle qualifie le lien, pas la personne** : la même personne est propriétaire ici et
     * locataire ailleurs. D'où la colonne sur la demande et non sur `Person` — exactement comme
     * `Inspection.met_person_capacity`, qui garde, elle, la qualité **constatée** le jour de la
     * visite (M32).
     */
    appointmentContactCapacity: z.string().nullable(),
    /**
     * La **note pour l'équipe** — « Commentaire · Note pour l'équipe (facultatif) », au bas de la
     * boîte « Attribuer & planifier ».
     *
     * **Un seul champ libre par demande**, et c'est la colonne `comment` du diagramme (la
     * « Commentaire » du legacy). Ce n'est pas un fil de discussion : les appels horodatés du
     * prototype sont un autre objet, qui n'a pas de table et n'en reçoit pas ici.
     */
    comment: z.string().nullable(),
    /** La date de la demande (#435) — affichée et exportée, elle ne fait partir aucun délai. */
    requestedOn: z.iso.date().nullable().default(null),
});
/** Un texte qu'une tablette d'avant sa descente relit absent : `null`, pas une purge du cache (D22). */
const cachedLabel = z
    .string()
    .nullish()
    .transform((value) => value ?? null);
/**
 * La vue « Mes contrôles » telle que l'API la sert au terrain : l'adresse et le rendez-vous sont
 * **aplatis**, la tablette n'ayant ni le site, ni la commune, ni la personne à recomposer pour
 * afficher une ligne de tournée.
 *
 * Chaque champ d'adresse est nullable : une demande importée d'un fichier client arrive parfois
 * sans rue ni code postal, et refuser de l'afficher priverait l'agent du seul repère qu'il a
 * (M21). L'objet `address`, lui, est exigé — c'est la forme, pas la complétude.
 *
 * ⚠️ **`scheduledAt` est nullable, lui, et c'est une différence de nature avec `address`** : une
 * demande sans adresse n'existe pas, une demande sans rendez-vous est l'état normal de tout ce
 * qui vient d'être reçu (M3). `null` veut dire « pas encore planifié », jamais « on ne sait pas ».
 *
 * ⚠️ **Le rendez-vous est à plat, pas dans un objet `appointment`.** Deux lectures en dépendent
 * déjà — l'index composé `[teamId+scheduledDay]` du cache terrain, qui dérive sa clé de
 * `scheduledAt`, et l'agenda qui s'ouvre dessus. L'imbriquer coûterait une version Dexie pour
 * ne rien ajouter : la tournée porte **un** rendez-vous, celui en vigueur.
 *
 * `rejectionReason` est la **consigne de correction du dernier renvoi**, quand la dernière
 * décision du bureau en est un (M25). `POST /validation/inquiries/:id/reject` ramène déjà la
 * demande dans la tournée — `in_progress` est servi —, mais muette : l'agent voyait son contrôle
 * revenir sans savoir pourquoi (#169). C'est le seul champ de cette vue qui ne vienne pas de
 * `Inquiry` : il est lu dans l'historique des décisions, et vaut `null` dès que la dernière n'est
 * pas un renvoi.
 *
 * ⚠️ **Le motif, et rien de la décision autour.** Ni l'auteur — c'est une donnée personnelle que
 * l'agent n'a pas à connaître pour corriger —, ni la date, que rien à l'écran n'exploiterait.
 * Le renvoi se raconte en back-office, où l'historique complet est.
 */
export const assignedInquirySchema = z.object({
    id: uuidV7Schema,
    reference: z.string().nullable(),
    caseReference: z.string().nullable(),
    status: inquiryStatusSchema,
    /**
     * Le **type de contrôle**, le **donneur d'ordre** et la **parcelle** — trois des six champs que
     * l'onglet « Infos » de la tablette affichait vides.
     *
     * ⚠️ **Ils descendent parce que le modèle les porte enfin**, pas parce que l'écran les
     * réclamait : `Inquiry.purpose` et `Inquiry.client_id` (M32), `Site.cadastral_reference`. Le
     * réseau les a rejoints (`sewerNetworkType`, M43) ; maître d'ouvrage et entreprise restent
     * vides, et le champ le dit.
     *
     * ⚠️ **Le client est un nom, pas un identifiant**, contrairement au back-office : la tablette
     * l'affiche et ne filtre rien dessus. Lui descendre une clé étrangère l'obligerait à porter un
     * référentiel de clients hors ligne pour afficher un mot.
     *
     * ⚠️ Une tablette dont le cache précède ces champs les relit `undefined` : `nullish` les ramène
     * à `null` plutôt que d'exiger une purge du cache (D22), comme `scheduledAt` avant eux.
     */
    purpose: z
        .string()
        .nullish()
        .transform((value) => value ?? null),
    clientName: z
        .string()
        .nullish()
        .transform((value) => value ?? null),
    cadastralReference: z
        .string()
        .nullish()
        .transform((value) => value ?? null),
    /**
     * Le **réseau public du dossier** (`Site.sewer_network_type`, M43) — « Réseau unitaire ·
     * Systématique » sur la carte de l'agenda, « Réseau » dans l'onglet Infos. `null` quand le
     * dossier ne le dit pas : l'écran le tait.
     *
     * ⚠️ **Pas le réseau constaté** (`survey.plot.publicNetworkType`), que l'agent relève sur place
     * et qui seul décide des anomalies : celui-ci annonce, il ne pré-remplit rien.
     *
     * ⚠️ Un code inconnu se relit `null` plutôt que de refuser la tournée — un serveur plus récent
     * que le bundle (M21), comme `tabletHiddenBlocks` ; absent d'une ligne mise en cache avant lui,
     * `null` aussi (D22).
     */
    sewerNetworkType: z
        .string()
        .nullish()
        .transform((value) => sewerNetworkTypeSchema.safeParse(value).data ?? null),
    /**
     * Le **lot** du lieu (`Site.lot_id`), « Secteur » à l'écran (#552) : son code et son nom, que le
     * filtre « Secteur » de « Mes contrôles » propose hors ligne (#580). `null` pour un lieu sans
     * lot — une vente, le plus souvent.
     *
     * ⚠️ **L'identifiant aussi, et c'est lui que le filtre compare** : le code n'est unique que chez
     * son client (`lotSchema`, M1), et deux clients d'une même tournée peuvent numéroter pareil. Le
     * code et le nom composent le libellé du bureau (« Secteur <code> — <nom> »). Une ligne mise en
     * cache avant ce champ le relit `undefined`, ramené à `null` (D22).
     */
    lot: z
        .object({ id: uuidV7Schema, code: z.string(), name: z.string() })
        .nullish()
        .transform((value) => value ?? null),
    /**
     * Le rendez-vous **en vigueur** — le plus tardif quand un report en a posé plusieurs (M24) —,
     * ou `null` quand aucun n'est pris. Descendu dans la tournée depuis #115 : sans lui la tablette
     * n'a rien à opposer au jour où l'agent dit avoir fait le contrôle, et l'écart reste invisible
     * là où il se constate.
     *
     * ⚠️ Une tablette dont le cache précède ce champ le relit `undefined` : les écrans le ramènent
     * à `null` à la lecture plutôt que d'exiger une purge du cache (D22).
     */
    scheduledAt: z.iso.datetime({ offset: true }).nullable(),
    /** La durée de ce rendez-vous, en minutes (#431) : la tablette en dit la fin. `null` sans lui. */
    appointmentDurationMinutes: z.int().positive().nullable().default(null),
    /**
     * L'étape de la suite qui a **imposé** ce rendez-vous — RDV1 ou RDV2 (#574) —, `null` pour un
     * rendez-vous convenu avec l'usager, ou sans rendez-vous. L'agenda et la pré-fiche le marquent.
     */
    appointmentImposedStep: followUpStepSchema.nullable().default(null),
    /** La note du bureau pour l'équipe (#433) — la pré-fiche la montre. `null` sans elle. */
    comment: z.string().nullable().default(null),
    /**
     * La case « Urgent » du bureau (Sentry BACK-OFFICE-X) : « Mes contrôles » la dit en rouge dès
     * la réception. Une ligne mise en cache avant ce champ le relit `undefined` : `false` (D22).
     */
    urgent: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    /**
     * Le type de bâti du lieu (#586) : l'agenda, la pré-fiche et « Infos » le disent. Un code
     * inconnu se lit `null`, comme `tabletHiddenBlocks` : la liste vient d'une feuille de GHP, et un
     * serveur plus récent que le bundle ne doit pas faire tomber la tournée (M21). ⚠️ Une ligne
     * mise en cache avant ce champ le relit `undefined`, comme `scheduledAt` : les écrans le
     * ramènent à `null` à la lecture (D22).
     */
    buildingType: buildingTypeSchema.nullable().catch(null),
    /**
     * **Le contact du rendez-vous** (`Inquiry.appointmentContactId`, M15) — celui que l'agent
     * appelle en arrivant, souvent une agence immobilière sur une vente. Ce n'est **pas** la
     * personne rencontrée pendant le contrôle : la pré-fiche s'ouvre avant la visite, et à cet
     * instant personne n'a encore été rencontré.
     *
     * `null` quand la demande n'en porte aucun — le cas courant d'un import client (M21) : la
     * tournée descend quand même, l'agent se présente sans appeler.
     *
     * ⚠️ **Quatre champs sur cinq.** L'identifiant de la personne ne descend pas : la tablette ne
     * peut rien en faire. L'**e-mail**, longtemps écarté comme surface RGPD sans usage, est entré le
     * 23/09/2026 avec l'écran qui **corrige** le contact en ligne (seconde exception à D4) : l'agent
     * ne corrige pas ce qu'il ne voit pas. ⚠️ **`string`, pas `z.email()`** : la base porte des
     * valeurs reprises hors format (« tel uniquement », cf. `usableEmail`), et un contrôle ici ferait
     * refuser la tournée entière pour une adresse.
     *
     * ⚠️ Une tablette dont le cache précède ce champ le relit `undefined`, comme `scheduledAt` :
     * les écrans le ramènent à `null` à la lecture plutôt que d'exiger une purge du cache (D22).
     */
    contact: personSchema
        .pick({ lastName: true, firstName: true, phone: true })
        /**
         * L'« **En qualité de** » — propriétaire, locataire, mandataire, gardien.
         *
         * ⚠️ **Elle vient de la demande, pas de la personne**, et elle voyage pourtant dans cet objet.
         * C'est délibéré : elle qualifie le **lien** de cette personne à cette adresse (`M32`), et un
         * champ à côté obligerait chaque écran à rapprocher deux valeurs qui ne se lisent que
         * ensemble — « Camille Renaud, locataire » est une seule information pour l'agent.
         *
         * ⚠️ **Ajoutée le 21/09/2026, sur un retour de test terrain** : l'agent arrivait devant la
         * maison sans savoir à qui il allait parler. Le commentaire ci-dessus disait « trois champs
         * sur cinq, délibérément » — il en fait quatre, et la raison des deux exclus n'a pas changé.
         */
        .extend({ email: z.string().nullable(), capacity: z.string().nullable() })
        .nullable(),
    /**
     * Le **propriétaire** du lieu (`Site.owner_id`, M15) — descendu pour que l'agent voie ce qu'il
     * corrige (`PATCH /inquiries/:id/contacts`) au lieu de le remplacer à l'aveugle.
     *
     * **Complet depuis #350** — adresse, commune et e-mail compris : l'onglet Infos montre le bloc
     * « Propriétaire » du rapport papier. `null` quand le lieu n'en porte pas — le cas courant d'une
     * demande saisie à la main. ⚠️ L'e-mail en `string`, comme celui du contact : la base porte des
     * valeurs hors format, et la tournée entière ne doit pas tomber pour une adresse.
     *
     * ⚠️ Une tablette dont le cache précède ce champ le relit `undefined`, comme `contact` : les
     * écrans le ramènent à `null` à la lecture (D22). Les trois champs de #350 se relisent `null`
     * sur un propriétaire mis en cache avant eux.
     */
    owner: personSchema
        .pick({ lastName: true, firstName: true, phone: true })
        .extend({ email: cachedLabel, address: cachedLabel, municipality: cachedLabel })
        .nullable(),
    address: inquiryAddressSchema,
    rejectionReason: z.string().nullable(),
    /**
     * Les blocs de la fiche que la tablette **ne demande pas** pour ce contrôle (#355, M33) : déduits
     * par le serveur des sections que la règle du client — sinon de son marché — retire du rapport
     * (`tabletHiddenBlocksOf`). La tablette n'a ni les règles ni le marché, et doit le savoir hors
     * réseau (D22).
     *
     * ⚠️ **Par ligne et non sur l'enveloppe**, à rebours de `allowAttachmentsAfterSubmission` : la
     * réponse change d'un client à l'autre dans une même tournée.
     *
     * ⚠️ Un code inconnu est **ignoré**, pas refusé : un serveur plus récent que le bundle ne doit pas
     * faire tomber la tournée (M21) — le bloc reste demandé. Absent d'une ligne mise en cache avant
     * #355, la fiche entière.
     */
    tabletHiddenBlocks: z
        .array(z.string())
        .nullish()
        .transform((codes) => (codes ?? []).filter((code) => tabletBlockSchema.safeParse(code).success)),
    /** Le rang de contre-visite — 1, 2… —, `null` pour un premier contrôle (#321, Q5). */
    followUpRank: z.number().int().positive().nullable().default(null),
    /**
     * Ce qu'une contre-visite reprend du contrôle qu'elle suit (#321) : son relevé, schéma compris,
     * et ses anomalies — la saisie validée la plus récente de la chaîne, avant elle. `null` hors
     * contre-visite, ou quand rien n'y a encore été validé : la fiche part alors vide.
     *
     * `media` (#675) : ses photos et pièces, **déjà dupliquées** dans le stockage pour la
     * contre-visite — chacune sous un identifiant à elle (`carriedMediumSchema`).
     */
    followUpSource: z
        .object({
        survey: surveyV2Schema,
        anomalies: z.array(submittedAnomalySchema),
        media: carriedMediaSchema,
    })
        .nullable()
        .default(null),
    /**
     * Ce que le client de la demande fait de chaque situation du tableau de GHP (#413) : la tablette
     * le recopie sur l'anomalie au moment du relevé, et c'est ce qui la fige. Même montage que
     * `tabletHiddenBlocks` : par ligne, une règle illisible ignorée (M21), aucune sur une ligne mise
     * en cache avant #413 — l'anomalie compte alors comme avant.
     */
    anomalyEffects: z
        .array(z.unknown())
        .nullish()
        .transform((rules) => (rules ?? []).flatMap((rule) => {
        const parsed = clientAnomalyEffectSchema.safeParse(rule);
        return parsed.success ? [parsed.data] : [];
    })),
    /**
     * Les **relances déposées** par les agents sur cette demande (M40) — l'état de relance : la
     * prochaine s'appelle « Relance n° » ce nombre plus un. `0` sur une ligne mise en cache avant.
     */
    remindersLeft: z.number().int().min(0).default(0),
    /**
     * La suite d'un avis de passage chez le client de la demande (M40), dans l'ordre — celle que la
     * liste « Avis de passage » du bureau montre. Vide : pas de suite, ou une ligne mise en cache avant.
     */
    passageFollowUp: z.array(followUpStepSchema).default([]),
    /** Les étapes de cette suite que le bureau a notées faites, sur les avis de la demande. */
    passageFollowUpDone: z.array(followUpStepSchema).default([]),
    /**
     * L'identifiant qu'un **contrôle libre** de cette équipe portait sur la tablette avant que le
     * bureau ne le rattache à cette demande (#419, M41) — `null` sinon, ou quand c'est le même.
     * C'est ce qui permet à la tablette, quand la demande lui revient renvoyée, de retrouver son
     * brouillon au lieu d'ouvrir une fiche vide.
     */
    freeInspectionId: uuidV7Schema.nullable().default(null),
});
/**
 * D24 — une liste se lit **paginée et comptée par le serveur**, jamais rendue en entier au
 * client. `total` porte sur l'ensemble filtré, pas sur `items` : c'est ce qui permet à la
 * tablette d'afficher « 3 sur 12 » et de savoir s'il reste une page à demander.
 *
 * ⚠️ `total` et `items.length` diffèrent dès la première page — les confondre est l'erreur que
 * ce contrat existe pour empêcher.
 */
export const assignedInquiryPageSchema = z.object({
    items: z.array(assignedInquirySchema),
    total: z.number().int().min(0),
    /**
     * Ce que l'organisation autorise à la tablette : peut-elle joindre une pièce **après** avoir
     * transmis un contrôle (M35) ?
     *
     * ⚠️ **Sur l'enveloppe et non sur chaque ligne** : c'est un réglage d'organisation, le même
     * pour les cinquante demandes d'une tournée. Le répéter par ligne serait cinquante copies d'un
     * booléen, qu'une réponse partielle pourrait faire diverger.
     *
     * ⚠️ **Servi ici plutôt que par `GET /settings/organization`**, et c'est une décision de
     * sécurité : cette route-là est réservée au bureau, et l'ouvrir aux tablettes exposerait tout
     * l'objet de réglages pour un booléen. La tournée est déjà ce que la tablette demande, et elle
     * dit exactement « ce que j'ai le droit de faire maintenant ».
     *
     * ⚠️ **Le serveur refuse de toute façon** (2.7, `409` nommé) : ce drapeau ne garde rien, il
     * évite un travail perdu. Une tablette dont le bundle précède ce champ le lira absent et
     * gardera l'ancien comportement — d'où le défaut, et non une exigence.
     */
    allowAttachmentsAfterSubmission: z.boolean().default(false),
    /**
     * Les demandes de l'équipe **transmises et pas encore tranchées** (`submitted`) — ce que le
     * bureau peut encore renvoyer au terrain.
     *
     * ⚠️ La tournée ne sert plus une demande transmise : sans cette liste, la tablette ne distingue
     * pas « en attente de validation » de « validé, supprimé ou confié à une autre équipe », et
     * garde à vie chaque contrôle transmis (#668). Hors de cette liste, un contrôle transmis et reçu
     * peut quitter la tablette ; dedans, il reste, pour qu'un renvoi se corrige sans tout ressaisir.
     *
     * Absente d'un serveur qui précède ce champ : la tablette garde alors tout ce qui est transmis.
     */
    awaitingValidation: z.array(z.string()).optional(),
});
//# sourceMappingURL=inquiry.js.map